"use client";
import { useState, useCallback } from "react";

export interface LineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
}

export interface Document {
  id: string;
  name: string;
  required: boolean;
  completed: boolean;
  url?: string;
}

export interface ApprovalFormData {
  vendor: string;
  description: string;
  amount: number;
  urgency: "low" | "medium" | "high";
  costCenter: string;
  lineItems: LineItem[];
  documents: Document[];
  notes: string;
}

export interface Step {
  id: string;
  label: string;
  description: string;
  status: "pending" | "in-progress" | "completed";
  canSkip: boolean;
}

export interface ApprovalController {
  currentStep: number;
  steps: Step[];
  formData: ApprovalFormData;
  loading: boolean;
  error: string | null;
  canProceed: boolean;
  canGoBack: boolean;
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateFormData: (data: Partial<ApprovalFormData>) => void;
  addLineItem: (item: LineItem) => void;
  removeLineItem: (itemId: string) => void;
  updateLineItem: (itemId: string, item: LineItem) => void;
  addDocument: (doc: Document) => void;
  removeDocument: (docId: string) => void;
  toggleDocumentCompletion: (docId: string) => void;
  submit: () => Promise<void>;
  isLastStep: boolean;
}

const initialSteps: Step[] = [
  { id: "details", label: "Request Details", description: "Vendor and basic information", status: "in-progress", canSkip: false },
  { id: "items", label: "Line Items", description: "Add itemized costs", status: "pending", canSkip: false },
  { id: "documents", label: "Documents", description: "Upload required documents", status: "pending", canSkip: false },
  { id: "review", label: "Review & Submit", description: "Verify all information", status: "pending", canSkip: false },
];

const initialFormData: ApprovalFormData = {
  vendor: "Acme Corporation",
  description: "Q4 Software License Renewal and Maintenance Services",
  amount: 45000,
  urgency: "medium",
  costCenter: "IT-001",
  lineItems: [
    { id: "1", description: "Software License", quantity: 1, unitPrice: 35000, totalPrice: 35000 },
    { id: "2", description: "Maintenance & Support", quantity: 1, unitPrice: 10000, totalPrice: 10000 },
  ],
  documents: [
    { id: "d1", name: "Vendor Invoice", required: true, completed: true, url: "/docs/invoice.pdf" },
    { id: "d2", name: "Purchase Order", required: true, completed: false },
    { id: "d3", name: "Compliance Certificate", required: false, completed: false },
  ],
  notes: "Annual renewal from approved vendor. Budget approved in Q4 planning.",
};

export function useApprovalController(): ApprovalController {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState(initialSteps);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isLastStep = currentStep === steps.length - 1;

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 0: // Details step
        return !!(formData.vendor && formData.description && formData.amount > 0 && formData.costCenter);
      case 1: // Line Items step
        return formData.lineItems.length > 0;
      case 2: // Documents step
        return formData.documents.filter((d) => d.required).every((d) => d.completed);
      case 3: // Review step
        return true;
      default:
        return false;
    }
  }, [currentStep, formData]);

  const canGoBack = currentStep > 0;

  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < steps.length) {
      setCurrentStep(step);
      const newSteps = steps.map((s, idx) => ({
        ...s,
        status: (idx === step ? "in-progress" : idx < step ? "completed" : "pending") as Step["status"],
      }));
      setSteps(newSteps);
    }
  }, [steps]);

  const nextStep = useCallback(() => {
    if (canProceed() && currentStep < steps.length - 1) {
      const newSteps = steps.map((s, idx) => ({
        ...s,
        status: (idx <= currentStep ? "completed" : idx === currentStep + 1 ? "in-progress" : "pending") as Step["status"],
      }));
      setSteps(newSteps);
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep, steps, canProceed]);

  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      const newSteps = steps.map((s, idx) => ({
        ...s,
        status: (idx < currentStep - 1 ? "completed" : "in-progress") as Step["status"],
      }));
      setSteps(newSteps);
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep, steps]);

  const updateFormData = useCallback((data: Partial<ApprovalFormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setError(null);
  }, []);

  const addLineItem = useCallback((item: LineItem) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, item],
    }));
  }, []);

  const removeLineItem = useCallback((itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.filter((item) => item.id !== itemId),
    }));
  }, []);

  const updateLineItem = useCallback((itemId: string, item: LineItem) => {
    setFormData((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((li) => (li.id === itemId ? item : li)),
    }));
  }, []);

  const addDocument = useCallback((doc: Document) => {
    setFormData((prev) => ({
      ...prev,
      documents: [...prev.documents, doc],
    }));
  }, []);

  const removeDocument = useCallback((docId: string) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.filter((doc) => doc.id !== docId),
    }));
  }, []);

  const toggleDocumentCompletion = useCallback((docId: string) => {
    setFormData((prev) => ({
      ...prev,
      documents: prev.documents.map((doc) => (doc.id === docId ? { ...doc, completed: !doc.completed } : doc)),
    }));
  }, []);

  const submit = useCallback(async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setError(null);
      // Return to indicate success
    } catch (err) {
      setError("Failed to submit approval request. Please try again.");
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    currentStep,
    steps,
    formData,
    loading,
    error,
    canProceed: canProceed(),
    canGoBack,
    goToStep,
    nextStep,
    previousStep,
    updateFormData,
    addLineItem,
    removeLineItem,
    updateLineItem,
    addDocument,
    removeDocument,
    toggleDocumentCompletion,
    submit,
    isLastStep,
  };
}
